/**
 * App registry — the source of truth for which "child apps" the frontend
 * knows how to render. The backend's `installed_apps` table is parallel: it
 * defines which apps the user *sees as installed*. An app must appear in
 * BOTH to render — the DB lookup tells the master which tiles to show, and
 * this registry tells the practice route which components to mount.
 *
 * To add a new app:
 *   1. Create `src/apps/<id>/CardFront.tsx` + `src/apps/<id>/index.ts`.
 *   2. Import + register it here.
 *   3. Add a migration that inserts a row into `installed_apps`.
 *   4. (Add an icon to `src/lib/app-icons.ts` if not already registered.)
 *
 * Payload heterogeneity: apps may carry different payload shapes (NounsPayload
 * for nouns/derdiedas, EinbuergerungsPayload for einbuergerungstest). The
 * registry value type is intentionally permissive — each AppDefinition
 * re-narrows its prop in its own CardFront. Callers using `getAppDefinition`
 * cast the payload at the boundary they care about.
 */
import { adjectivesApp } from './adjectives';
import { adjetivosApp } from './adjetivos';
import { derDieDasApp } from './derdiedas';
import { einbuergerungsApp } from './einbuergerungstest';
import { gramaticaApp } from './gramatica';
import { gramaticaCourse, toLessonView as gramaticaLessonView } from './gramatica/course';
import type { GramaticaLesson } from './gramatica/types';
import { grammatikApp } from './grammatik';
import { grammatikCourse, toLessonView as grammatikLessonView } from './grammatik/course';
import type { GrammatikLesson } from './grammatik/types';
import type { GrammarCourse, GrammarLessonView } from './shared/grammar/types';
import { konjugationApp } from './konjugation';
import { vokabelnApp } from './einbuergerungstest-vokabeln';
import { nounsApp } from './nouns';
import { generosApp } from './generos';
import { substantivosApp } from './substantivos';
import { verbosApp } from './verbos';
import { verbsApp } from './verbs';
import type { AppDefinition } from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyAppDefinition = AppDefinition<any>;

export const APP_REGISTRY: Record<string, AnyAppDefinition> = {
  nouns: nounsApp,
  derdiedas: derDieDasApp,
  einbuergerungstest: einbuergerungsApp,
  'einbuergerungstest-vokabeln': vokabelnApp,
  verbs: verbsApp,
  konjugation: konjugationApp,
  grammatik: grammatikApp,
  gramatica: gramaticaApp,
  substantivos: substantivosApp,
  generos: generosApp,
  adjectives: adjectivesApp,
  verbos: verbosApp,
  adjetivos: adjetivosApp,
};

export function getAppDefinition(appId: string): AnyAppDefinition | null {
  return APP_REGISTRY[appId] ?? null;
}

/**
 * Apps whose content is a curriculum rather than a deck of words.
 *
 * They share a route, a session shape and a set of components, and they are the
 * only apps that start a session through their own endpoint — the deck is
 * ordered rather than sampled. Listed here rather than tested for with
 * `appId === 'grammatik'` in three files, which is what this replaced.
 */
const GRAMMAR_COURSES: Record<string, GrammarCourseBinding> = {
  grammatik: {
    displayName: 'Grammatik',
    course: grammatikCourse,
    toLessonView: (lesson) => grammatikLessonView(lesson as GrammatikLesson),
  },
  gramatica: {
    displayName: 'Gramática',
    course: gramaticaCourse,
    toLessonView: (lesson) => gramaticaLessonView(lesson as GramaticaLesson),
  },
};

/**
 * What a route needs to render one course's lesson without knowing its
 * language: the course descriptor, and the adapter from its stored shape.
 */
export type GrammarCourseBinding = {
  displayName: string;
  course: GrammarCourse;
  toLessonView: (lesson: unknown) => GrammarLessonView;
};

export function isGrammarCourse(appId: string | undefined): appId is string {
  return appId !== undefined && appId in GRAMMAR_COURSES;
}

export function grammarCourseFor(appId: string | undefined): GrammarCourseBinding | null {
  return appId ? (GRAMMAR_COURSES[appId] ?? null) : null;
}
